import React, { useState, useEffect } from 'react';
import { Upload, RotateCcw, AlertCircle, X } from 'lucide-react';
import { authService } from '../services/authService';
import { getApiBaseUrl } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useAdminToast } from './admin/AdminToast';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const validateJsonContent = (content) => {
  if (!content || !content.trim()) {
    return 'File content cannot be empty';
  }

  try {
    JSON.parse(content);
    return '';
  } catch (err) {
    return 'File content must be valid JSON';
  }
};

const FileUploadManagement = () => {
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showSuccess, toastElement } = useAdminToast();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [submitting, setSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [formData, setFormData] = useState({
    fileName: '',
    fileContent: '',
    leadCategoryId: ''
  });
  const [newFileData, setNewFileData] = useState({
    fileName: '',
    fileContent: '',
    leadCategoryId: ''
  });
  const [uploadingNewFile, setUploadingNewFile] = useState(false);
  const [newFileError, setNewFileError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setError('');
    try {
      const [filesResponse, categoriesResponse] = await Promise.all([
        fetch(`${getApiBaseUrl()}/admin/uploaded-files`, {
          headers: authService.getAuthHeaders()
        }),
        fetch(`${getApiBaseUrl()}/admin/lead-categories/active`, {
          headers: authService.getAuthHeaders()
        })
      ]);

      if (filesResponse.ok && categoriesResponse.ok) {
        const filesData = await filesResponse.json();
        const categoriesData = await categoriesResponse.json();
        setFiles(filesData);
        setCategories(categoriesData);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      setError('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileRead = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const validationError = validateJsonContent(content);
        setError(validationError);
        setFormData({
          ...formData,
          fileName: file.name,
          fileContent: content
        });
      };
      reader.readAsText(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const validationError = validateJsonContent(formData.fileContent);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const url = editingFile 
        ? `${getApiBaseUrl()}/admin/uploaded-files/${editingFile.id}`
        : `${getApiBaseUrl()}/admin/uploaded-files`;
      
      const method = editingFile ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await fetchData();
        handleCloseDialog();
        setError('');
        showSuccess(`File "${formData.fileName}" ${editingFile ? 'updated' : 'uploaded'}`);
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to save file');
      }
    } catch (err) {
      setError('Error saving file');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (file) => {
    setError('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/uploaded-files/${file.id}`, {
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        const fileData = await response.json();
        setEditingFile(file);
        setFormData({
          fileName: fileData.originalFileName,
          fileContent: fileData.fileContent || '',
          leadCategoryId: fileData.leadCategory?.id || ''
        });
        setOpenDialog(true);
      } else {
        setError('Failed to fetch file details');
      }
    } catch (err) {
      setError('Error fetching file details');
    }
  };

  const handleDelete = async (id) => {
    const file = files.find((f) => f.id === id);
    const label = file ? file.originalFileName : 'this file';
    const confirmed = await confirm({
      title: `Delete file "${label}"?`,
      message: `"${label}" will be permanently deleted.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setError('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/uploaded-files/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        await fetchData();
        setError('');
        showSuccess(`File "${label}" deleted`);
      } else {
        setError('Failed to delete file');
      }
    } catch (err) {
      setError('Error deleting file');
    }
  };

  const handleMarkProcessed = async (id) => {
    const file = files.find((f) => f.id === id);
    setError('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/uploaded-files/${id}/mark-processed`, {
        method: 'PUT',
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        await fetchData();
        setError('');
        showSuccess(file ? `File "${file.originalFileName}" marked as processed` : 'File marked as processed');
      } else {
        setError('Failed to mark file as processed');
      }
    } catch (err) {
      setError('Error marking file as processed');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFile(null);
    setFormData({
      fileName: '',
      fileContent: '',
      leadCategoryId: ''
    });
    setError('');
  };

  const handleNewFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewFileData(prev => ({ ...prev, fileName: file.name }));
      setNewFileError('');
      
      // Read file content
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const validationError = validateJsonContent(content);
          setNewFileData(prev => ({ ...prev, fileContent: content }));
          setNewFileError(validationError);
        } catch (err) {
          setNewFileError('Error reading file content');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleNewFileSubmit = async (e) => {
    e.preventDefault();
    if (!newFileData.fileName || !newFileData.fileContent || !newFileData.leadCategoryId) {
      setNewFileError('Please fill in all fields');
      return;
    }

    const validationError = validateJsonContent(newFileData.fileContent);
    if (validationError) {
      setNewFileError(validationError);
      return;
    }

    setUploadingNewFile(true);
    setNewFileError('');

    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/uploaded-files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify({
          fileName: newFileData.fileName,
          fileContent: newFileData.fileContent,
          leadCategoryId: newFileData.leadCategoryId
        })
      });

      if (response.ok) {
        await fetchData();
        showSuccess(`File "${newFileData.fileName}" uploaded`);
        setNewFileData({ fileName: '', fileContent: '', leadCategoryId: '' });
        setNewFileError('');
        setUploadingNewFile(false);
      } else {
        const errorData = await response.text();
        setNewFileError(errorData || 'Failed to upload file');
        setUploadingNewFile(false);
      }
    } catch (err) {
      setNewFileError('Error uploading file');
      setUploadingNewFile(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    return (bytes / 1024).toFixed(2) + ' KB';
  };

  if (loading) return <AdminLoading message="Loading uploaded files..." />;

  return (
    <div className="space-y-6">
      {toastElement}
      {confirmDialog}
      <AdminPageHeader
        icon={Upload}
        title="File Upload Management"
        subtitle="Manage uploaded JSON lead files"
        rightContent={<AdminAddButton onClick={fetchData} label="Refresh Files" icon={RotateCcw} />}
      />

      {error && !openDialog && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      {/* Upload New File Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Upload New File</h2>
        <form onSubmit={handleNewFileSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">File Name</label>
            <input
              type="text"
              value={newFileData.fileName}
              onChange={(e) => setNewFileData(prev => ({ ...prev, fileName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter file name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Choose File</label>
            <input
              type="file"
              accept=".json,application/json"
              onChange={handleNewFileSelect}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Lead Category</label>
            <select
              value={newFileData.leadCategoryId}
              onChange={(e) => setNewFileData(prev => ({ ...prev, leadCategoryId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select a category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          {newFileError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
                <div className="text-sm text-red-800">{newFileError}</div>
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setNewFileData({ fileName: '', fileContent: '', leadCategoryId: '' })}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={uploadingNewFile}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            >
              {uploadingNewFile ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Original Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {files.map((file) => (
              <tr key={file.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{file.originalFileName}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {file.leadCategory?.name || 'Unknown'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <AdminRowActions align="start">
                    <AdminActionButton variant="edit" onClick={() => handleEdit(file)} />
                    {!file.processed && (
                      <AdminActionButton variant="info" icon={Upload} label="Mark Processed" title="Mark as Processed" onClick={() => handleMarkProcessed(file.id)} />
                    )}
                    <AdminActionButton variant="delete" onClick={() => handleDelete(file.id)} />
                  </AdminRowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {editingFile ? 'Edit File' : 'Upload File'}
              </h3>
              <button
                onClick={handleCloseDialog}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {error && (
              <div role="alert" className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" />
                <div className="text-sm text-red-800">{error}</div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="mt-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">File Name</label>
                <input
                  type="text"
                  value={formData.fileName}
                  disabled={!!editingFile}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {editingFile ? 'Upload New File (replaces current content)' : 'Choose File'}
                </label>
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileRead}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead Category</label>
                <select
                  value={formData.leadCategoryId}
                  onChange={(e) => setFormData({ ...formData, leadCategoryId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({category.code})
                    </option>
                  ))}
                </select>
              </div>
              {formData.fileContent && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {editingFile ? 'File Content' : 'File Content Preview'}
                  </label>
                  <textarea
                    value={formData.fileContent}
                    onChange={(e) => setFormData({ ...formData, fileContent: e.target.value })}
                    disabled={!editingFile}
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
                  />
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingFile ? 'Updating...' : 'Uploading...'}
                    </>
                  ) : (
                    editingFile ? 'Update' : 'Upload'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadManagement;
